import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://clintonncpressurewashing.com";
// Dedicated 1200x630 JPEG. Social crawlers want a wide, fixed-ratio image, and
// several still handle WebP poorly, so this one stays JPEG.
const DEFAULT_OG_IMAGE = "/img/og-image.jpg";
const STRUCTURED_DATA_ID = "route-structured-data";

const toAbsoluteUrl = (pathOrUrl) => {
  if (!pathOrUrl) {
    return `${SITE_URL}${DEFAULT_OG_IMAGE}`;
  }

  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  return `${SITE_URL}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
};

const upsertMeta = (attribute, key, content) => {
  if (!content) {
    return;
  }

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
};

const upsertStructuredData = (schemaData) => {
  const existingScript = document.getElementById(STRUCTURED_DATA_ID);

  if (!schemaData) {
    if (existingScript) {
      existingScript.remove();
    }
    return;
  }

  let normalizedSchema = schemaData;

  if (Array.isArray(schemaData)) {
    normalizedSchema = {
      "@context": "https://schema.org",
      "@graph": schemaData
        .filter(Boolean)
        .map((item) => {
          if (!item || typeof item !== "object") {
            return item;
          }

          const clone = { ...item };
          delete clone["@context"];
          return clone;
        }),
    };
  }

  const script = existingScript || document.createElement("script");
  script.setAttribute("id", STRUCTURED_DATA_ID);
  script.setAttribute("type", "application/ld+json");
  script.text = JSON.stringify(normalizedSchema);

  if (!existingScript) {
    document.head.appendChild(script);
  }
};

export const SEO = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  schemaData = null,
  robots = "index, follow",
}) => {
  const location = useLocation();

  useEffect(() => {
    const normalizedPath =
      location.pathname !== "/" && location.pathname.endsWith("/")
        ? location.pathname.slice(0, -1)
        : location.pathname;

    const canonicalUrl = `${SITE_URL}${normalizedPath || "/"}`;
    const resolvedTitle =
      title || "Hamilton Pressure Washing | Pressure Washing Clinton NC";
    const resolvedDescription =
      description ||
      "Pressure washing, soft washing, and exterior cleaning services in Clinton, NC.";
    const resolvedImage = toAbsoluteUrl(image);

    document.title = resolvedTitle;
    upsertMeta("name", "description", resolvedDescription);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:title", resolvedTitle);
    upsertMeta("property", "og:description", resolvedDescription);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", resolvedImage);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", resolvedTitle);
    upsertMeta("name", "twitter:description", resolvedDescription);
    upsertMeta("name", "twitter:image", resolvedImage);
    upsertCanonical(canonicalUrl);
    upsertStructuredData(schemaData);
  }, [description, image, location.pathname, robots, schemaData, title]);

  return null;
};
